/**
 * User Groups — Settings › User & Access › User Groups.
 *
 * Create a group, add users to it, and grant it applications — every member
 * then has the same view/use access an admin could grant one user at a
 * time via a module's own Share dialog, but for the whole group at once,
 * and staying current automatically: adding someone to a group that already
 * holds ten apps gives them all ten immediately (resolved server-side, see
 * modules_router.py).
 *
 * List and detail are two screens of the one tab, the same pattern
 * LayersRegistryTab uses — a group's members/modules are only fetched once
 * it is actually opened, not for every row in the list.
 *
 * Every signed-in user reaches this tab now (see SettingsPage), but only
 * someone holding `groups:manage` (or admin) can actually create a group or
 * open one's detail — the server gates the same way (`groups_router.py`'s
 * own doc comment), this is just not offering what would 403 anyway. The
 * plain list (name, member count, app count — nothing about WHO is in a
 * group) stays visible to everyone: `GET /groups` itself needs no
 * permission.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  Check,
  Layers as ModuleIcon,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users as UsersIcon,
  X,
} from 'lucide-react';
import type { AppModule, Group, GroupDetail, User } from '../../types';
import { groupService } from '../../services/groupService';
import { userService } from '../../services/userService';
import { isDashboard } from '../../dashboard/services/dashboardService';
import { usePermissions } from '../../auth/AuthContext';

export default function UserGroupsTab({ modules }: { modules: AppModule[] }) {
  const { isAdmin, can } = usePermissions();
  const canManage = isAdmin || can('groups:manage');
  const [groups, setGroups] = useState<Group[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = async () => {
    setLoadError(null);
    try {
      setGroups(await groupService.list());
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Could not load groups.');
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const createGroup = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreateError(null);
    try {
      const created = await groupService.create(name);
      setNewName('');
      setCreating(false);
      await load();
      setSelectedId(created.id);
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'Could not create this group.');
    }
  };

  if (selectedId) {
    return (
      <GroupDetailView
        groupId={selectedId}
        modules={modules}
        onBack={() => setSelectedId(null)}
        onChanged={(patch) =>
          setGroups((current) =>
            current?.map((g) => (g.id === selectedId ? { ...g, ...patch } : g)) ?? current,
          )
        }
        onDeleted={() => {
          setSelectedId(null);
          void load();
        }}
      />
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-[11.5px] text-slate-500">
        A group's members get whatever applications the group is granted — view and use, the same
        as sharing an app with one user, but for everyone in the group at once.
      </p>

      {loadError && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
          {loadError}
        </p>
      )}

      {!canManage && (
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] leading-relaxed text-slate-500">
          You can see which groups exist, but creating one or managing its members and applications
          needs the "Create and manage user groups" permission.
        </p>
      )}

      {canManage &&
        (creating ? (
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2.5">
            <input
              autoFocus
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void createGroup();
                if (event.key === 'Escape') {
                  setCreating(false);
                  setNewName('');
                  setCreateError(null);
                }
              }}
              placeholder="Group name…"
              className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[12.5px] text-slate-800 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
            />
            <button
              type="button"
              onClick={() => void createGroup()}
              disabled={!newName.trim()}
              className="rounded-lg bg-[#0F2942] px-3 py-1.5 text-[12px] font-semibold text-white transition hover:bg-[#16395C] disabled:opacity-40"
            >
              Create
            </button>
            <button
              type="button"
              onClick={() => {
                setCreating(false);
                setNewName('');
                setCreateError(null);
              }}
              aria-label="Cancel"
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0F2942] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#16395C]"
          >
            <Plus className="h-3.5 w-3.5" />
            Create group
          </button>
        ))}
      {createError && (
        <p role="alert" className="text-[11px] font-medium text-red-600">
          {createError}
        </p>
      )}

      {groups === null ? (
        <p className="py-8 text-center text-sm text-slate-400">Loading groups…</p>
      ) : groups.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 px-3 py-10 text-center text-sm text-slate-400">
          No groups yet. Create one to share applications with several users at once.
        </p>
      ) : (
        <ul className="space-y-2">
          {groups.map((group) => {
            const row = (
              <>
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100">
                  <UsersIcon className="h-4 w-4 text-slate-400" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium text-slate-800">
                    {group.name}
                  </span>
                  <span className="mt-0.5 block text-[11px] text-slate-400">
                    {group.memberCount} member{group.memberCount === 1 ? '' : 's'} ·{' '}
                    {group.moduleCount} app{group.moduleCount === 1 ? '' : 's'}
                  </span>
                </span>
              </>
            );
            return (
              <li key={group.id}>
                {canManage ? (
                  <button
                    type="button"
                    onClick={() => setSelectedId(group.id)}
                    className="flex w-full items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-left transition hover:border-blue-300 hover:bg-blue-50"
                  >
                    {row}
                  </button>
                ) : (
                  <div className="flex w-full items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
                    {row}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function GroupDetailView({
  groupId,
  modules,
  onBack,
  onChanged,
  onDeleted,
}: {
  groupId: string;
  modules: AppModule[];
  onBack: () => void;
  /** Keeps the list's counts in step without a full re-fetch. */
  onChanged: (patch: Partial<Group>) => void;
  onDeleted: () => void;
}) {
  const [group, setGroup] = useState<GroupDetail | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [memberIds, setMemberIds] = useState<Set<string>>(new Set());
  const [moduleIds, setModuleIds] = useState<Set<string>>(new Set());
  const [userQuery, setUserQuery] = useState('');
  const [moduleQuery, setModuleQuery] = useState('');
  const [savingMembers, setSavingMembers] = useState(false);
  const [savingModules, setSavingModules] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    Promise.all([groupService.get(groupId), userService.listUsers()])
      .then(([detail, allUsers]) => {
        if (cancelled) return;
        setGroup(detail);
        setUsers(allUsers.filter((user) => user.role !== 'admin'));
        setMemberIds(new Set(detail.memberIds));
        setModuleIds(new Set(detail.moduleIds));
        setNameDraft(detail.name);
      })
      .catch((caught) => {
        if (!cancelled) setLoadError(caught instanceof Error ? caught.message : 'Could not load this group.');
      });
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  const filteredUsers = useMemo(() => {
    const term = userQuery.trim().toLowerCase();
    if (!term) return users;
    return users.filter(
      (user) => user.name.toLowerCase().includes(term) || user.email.toLowerCase().includes(term),
    );
  }, [users, userQuery]);

  const filteredModules = useMemo(() => {
    const term = moduleQuery.trim().toLowerCase();
    if (!term) return modules;
    return modules.filter((module) => (module.title || module.name || '').toLowerCase().includes(term));
  }, [modules, moduleQuery]);

  const toggleMember = (userId: string) =>
    setMemberIds((current) => {
      const next = new Set(current);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  const toggleModule = (moduleId: string) =>
    setModuleIds((current) => {
      const next = new Set(current);
      if (next.has(moduleId)) next.delete(moduleId);
      else next.add(moduleId);
      return next;
    });

  const membersDirty = group ? memberIds.size !== group.memberIds.length || group.memberIds.some((id) => !memberIds.has(id)) : false;
  const modulesDirty = group ? moduleIds.size !== group.moduleIds.length || group.moduleIds.some((id) => !moduleIds.has(id)) : false;

  const saveMembers = async () => {
    setSavingMembers(true);
    setError(null);
    try {
      const updated = await groupService.setMembers(groupId, Array.from(memberIds));
      setGroup(updated);
      onChanged({ memberCount: updated.memberCount });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not save members.');
    } finally {
      setSavingMembers(false);
    }
  };

  const saveModules = async () => {
    setSavingModules(true);
    setError(null);
    try {
      const updated = await groupService.setModules(groupId, Array.from(moduleIds));
      setGroup(updated);
      onChanged({ moduleCount: updated.moduleCount });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not save applications.');
    } finally {
      setSavingModules(false);
    }
  };

  const saveName = async () => {
    const name = nameDraft.trim();
    if (!name || !group || name === group.name) {
      setRenaming(false);
      return;
    }
    try {
      const updated = await groupService.rename(groupId, name);
      setGroup(updated);
      onChanged({ name: updated.name });
      setRenaming(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not rename this group.');
    }
  };

  const remove = async () => {
    if (!group) return;
    if (!window.confirm(`Delete the group "${group.name}"? This does not delete any application — only the group.`)) {
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      await groupService.remove(groupId);
      onDeleted();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not delete this group.');
      setDeleting(false);
    }
  };

  if (loadError) {
    return (
      <div className="space-y-3">
        <BackLink onBack={onBack} />
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
          {loadError}
        </p>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="space-y-3">
        <BackLink onBack={onBack} />
        <p className="py-8 text-center text-sm text-slate-400">Loading…</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <BackLink onBack={onBack} />
        <button
          type="button"
          onClick={() => void remove()}
          disabled={deleting}
          className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-2.5 py-1.5 text-[11.5px] font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
          {deleting ? 'Deleting…' : 'Delete group'}
        </button>
      </div>

      <div>
        {renaming ? (
          <div className="flex items-center gap-2">
            <input
              autoFocus
              value={nameDraft}
              onChange={(event) => setNameDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void saveName();
                if (event.key === 'Escape') {
                  setNameDraft(group.name);
                  setRenaming(false);
                }
              }}
              className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-base font-semibold text-slate-900 outline-none focus:border-teal-400"
            />
            <button
              type="button"
              onClick={() => void saveName()}
              aria-label="Save name"
              className="rounded-lg p-1.5 text-teal-600 hover:bg-teal-50"
            >
              <Check className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setRenaming(true)}
            className="group flex items-center gap-1.5 text-base font-semibold text-slate-900"
          >
            {group.name}
            <Pencil className="h-3.5 w-3.5 text-slate-300 opacity-0 transition group-hover:opacity-100" />
          </button>
        )}
      </div>

      {error && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
          {error}
        </p>
      )}

      {/* ── Members ── */}
      <section className="space-y-2.5">
        <div className="flex items-center justify-between">
          <h4 className="flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-700">
            <UsersIcon className="h-4 w-4 text-slate-400" />
            Members ({memberIds.size})
          </h4>
          <button
            type="button"
            onClick={() => void saveMembers()}
            disabled={!membersDirty || savingMembers}
            className="rounded-lg bg-[#0F2942] px-3 py-1.5 text-[11.5px] font-semibold text-white transition hover:bg-[#16395C] disabled:opacity-40"
          >
            {savingMembers ? 'Saving…' : 'Save members'}
          </button>
        </div>

        <label className="relative block">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input
            value={userQuery}
            onChange={(event) => setUserQuery(event.target.value)}
            placeholder="Search users…"
            className="w-full rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-3 text-[12.5px] text-slate-800 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
          />
        </label>

        {users.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-300 py-8 text-center text-xs text-slate-400">
            No non-admin users exist yet. Create one in Settings → User Management.
          </p>
        ) : (
          <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
            {filteredUsers.map((user) => {
              const checked = memberIds.has(user.id);
              return (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => toggleMember(user.id)}
                  className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition ${
                    checked ? 'bg-teal-50' : 'hover:bg-slate-50'
                  }`}
                >
                  <span
                    className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                      checked ? 'border-teal-500 bg-teal-500' : 'border-slate-300 bg-white'
                    }`}
                  >
                    {checked && <Check className="h-3 w-3 text-white" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium text-[#0F2942]">{user.name}</span>
                    <span className="block truncate text-[11px] text-slate-400">{user.email}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Applications ── */}
      <section className="space-y-2.5 border-t border-slate-100 pt-4">
        <div className="flex items-center justify-between">
          <h4 className="flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-700">
            <ModuleIcon className="h-4 w-4 text-slate-400" />
            Applications ({moduleIds.size})
          </h4>
          <button
            type="button"
            onClick={() => void saveModules()}
            disabled={!modulesDirty || savingModules}
            className="rounded-lg bg-[#0F2942] px-3 py-1.5 text-[11.5px] font-semibold text-white transition hover:bg-[#16395C] disabled:opacity-40"
          >
            {savingModules ? 'Saving…' : 'Save applications'}
          </button>
        </div>

        <label className="relative block">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input
            value={moduleQuery}
            onChange={(event) => setModuleQuery(event.target.value)}
            placeholder="Search applications…"
            className="w-full rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-3 text-[12.5px] text-slate-800 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
          />
        </label>

        {modules.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-300 py-8 text-center text-xs text-slate-400">
            No applications yet.
          </p>
        ) : (
          <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
            {filteredModules.map((module) => {
              const checked = moduleIds.has(module.id);
              return (
                <button
                  key={module.id}
                  type="button"
                  onClick={() => toggleModule(module.id)}
                  className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition ${
                    checked ? 'bg-teal-50' : 'hover:bg-slate-50'
                  }`}
                >
                  <span
                    className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                      checked ? 'border-teal-500 bg-teal-500' : 'border-slate-300 bg-white'
                    }`}
                  >
                    {checked && <Check className="h-3 w-3 text-white" />}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-xs font-medium text-[#0F2942]">
                    {module.title || module.name}
                  </span>
                  <span className="flex-shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
                    {isDashboard(module) ? 'Dashboard' : 'Map'}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function BackLink({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-blue-600 hover:text-blue-800"
    >
      <ArrowLeft className="h-3.5 w-3.5" />
      Back to all groups
    </button>
  );
}
